var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import React, { useCallback, useEffect, useState } from 'react';
import { AIGeneralBlockCard } from '../../AIGeneralBlockCard';
import { Text } from '../../../atoms/Text/Text';
import { useOptionalAIAgentMessage } from '../../AIAgentMessage/context';
import { submitResolutionConfirmation } from '../../../../features/AIChatWidget/services/chatApi';
import './ResolutionConfirmationRendererStyles.scss';
const baseClass = 'qmb-ui-ResolutionConfirmationRenderer';
const DEFAULT_PROMPT = 'Did this solve your issue?';
const ACK_RESOLVED = 'Thanks — glad that helped.';
const ACK_UNRESOLVED = "Thanks for letting us know. Tell me more and I'll keep working on it.";
/**
 * Delivery retries for the resolution post.
 *
 * The UI claim below is deliberately optimistic, so the post is the only thing
 * that can still lose the customer's answer. R12's verified_resolved_rate is
 * computed from these rows: a dropped post does not just lose one datapoint, it
 * biases the metric, because a failure is silent and indistinguishable from
 * "never asked". A bounded retry keeps the click feedback instant while making
 * a transient hiccup non-fatal.
 */
const RESOLUTION_POST_ATTEMPTS = 3;
const RESOLUTION_RETRY_BASE_MS = 500;
const delay = (ms) => __awaiter(void 0, void 0, void 0, function* () {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
});
/**
 * Session-scoped map of confirmation_id → Yes/No.
 *
 * The streaming message remounts into a persisted agent message on stream_end;
 * React local state alone is lost on that remount and previously re-offered
 * Yes/No. Module state survives remount within the tab. History reload strips
 * the block server-side (ephemeral), so a refresh never re-shows it.
 */
const answeredConfirmationIds = new Map();
/**
 * Subscribers per confirmation_id, so releasing a claim can reach renderers that
 * are ALREADY mounted.
 *
 * Deleting the map entry alone is not enough: a renderer seeds `useState` from
 * the map once (`prior`) and thereafter holds the answer in local state, and the
 * streaming message remounts into the persisted one on stream_end — so after a
 * failed delivery there can be several live renderers for the same id, each with
 * its own copy of the claim. Without this they would keep showing an
 * acknowledgment for an answer that was never delivered.
 */
const answerListeners = new Map();
function subscribeToAnswer(confirmationId, fn) {
    var _a;
    const listeners = (_a = answerListeners.get(confirmationId)) !== null && _a !== void 0 ? _a : new Set();
    listeners.add(fn);
    answerListeners.set(confirmationId, listeners);
    return () => {
        listeners.delete(fn);
        if (listeners.size === 0)
            answerListeners.delete(confirmationId);
    };
}
/**
 * Give the prompt back after delivery has definitively failed.
 *
 * The claim is deliberately HELD for the duration of the retries: it is what
 * stops a remount mid-loop from starting a second concurrent submission. It is
 * only released once every attempt has failed, at which point re-offering is the
 * lesser evil — `POST /api/chat/{id}/resolution` claims by confirmation_id with
 * Redis `SET NX`, so a resubmit of an answer the server did in fact record is
 * de-duped server-side rather than counted twice.
 */
function releaseAnswerClaim(confirmationId) {
    var _a;
    answeredConfirmationIds.delete(confirmationId);
    (_a = answerListeners.get(confirmationId)) === null || _a === void 0 ? void 0 : _a.forEach((fn) => fn(null));
}
/** Test-only: clear answered claims between Jest cases. */
export function resetAnsweredResolutionsForTests() {
    answeredConfirmationIds.clear();
    answerListeners.clear();
}
/**
 * ResolutionConfirmationRenderer
 *
 * Inline "did this solve your issue?" prompt, so resolution is asked rather than
 * assumed (R10/R12). Structurally a sibling of `ConfirmationRenderer` — same
 * two-button inline card — but semantically different: `ConfirmationRenderer` gates a
 * pending write, this one records whether an already-delivered answer worked.
 *
 * Also distinct from the generic per-message thumbs feedback (`action_bar` →
 * `POST /api/chat/{conversation_id}/feedback`), which rates a message. R12 needs to
 * tell "the AI thinks it resolved this" apart from "the customer confirmed it did",
 * which is why the Yes/No carries `confirmation_id` (the `ai_messages.id` of the
 * answer being confirmed).
 *
 * After either click the buttons drop and an acknowledgment stays visible for the
 * rest of the turn/session. The block is ephemeral on the orchestrator (not stored
 * in history), so a refresh does not re-offer it. A session claim prevents
 * stream_end remount from making the buttons clickable again.
 *
 * The one exception is delivery failing outright: after every retry has failed the
 * claim is released and the prompt is offered again, because an acknowledgment
 * standing for an answer the server never received is worse than asking twice.
 *
 * @version 1.3.0
 * @since 2026-07-30
 */
export function ResolutionConfirmationRenderer({ block }) {
    var _a, _b;
    const resolution = block;
    const agentMessage = useOptionalAIAgentMessage();
    const conversationId = agentMessage === null || agentMessage === void 0 ? void 0 : agentMessage.envelope.conversation_id;
    const prior = (_a = answeredConfirmationIds.get(resolution.confirmation_id)) !== null && _a !== void 0 ? _a : null;
    const [answer, setAnswer] = useState(prior);
    // Lets releaseAnswerClaim reach this instance even though `answer` was seeded
    // from the map once and now lives in local state.
    useEffect(() => subscribeToAnswer(resolution.confirmation_id, setAnswer), [resolution.confirmation_id]);
    const handleAnswer = useCallback((resolved) => __awaiter(this, void 0, void 0, function* () {
        if (answer !== null || answeredConfirmationIds.has(resolution.confirmation_id)) {
            return;
        }
        const next = resolved ? 'resolved' : 'unresolved';
        // Claim + acknowledge immediately — the customer's click is the signal, and a
        // network hiccup on a telemetry post should not leave the prompt looking
        // unclicked (or reappear after stream_end remounts the message).
        answeredConfirmationIds.set(resolution.confirmation_id, next);
        setAnswer(next);
        if (!conversationId) {
            console.error('[ResolutionConfirmationRenderer] No conversation_id in context; not posting.');
            return;
        }
        for (let attempt = 1; attempt <= RESOLUTION_POST_ATTEMPTS; attempt += 1) {
            try {
                // eslint-disable-next-line no-await-in-loop
                yield submitResolutionConfirmation(conversationId, {
                    confirmation_id: resolution.confirmation_id,
                    resolved
                });
                return;
            }
            catch (error) {
                if (attempt === RESOLUTION_POST_ATTEMPTS) {
                    console.error(`[ResolutionConfirmationRenderer] Resolution post failed after ${RESOLUTION_POST_ATTEMPTS} attempts; ` +
                        're-offering the prompt so the answer is not lost silently:', error);
                    // Hand the prompt back rather than leaving an acknowledgment standing
                    // for an answer that never reached the server.
                    releaseAnswerClaim(resolution.confirmation_id);
                    return;
                }
                // eslint-disable-next-line no-await-in-loop
                yield delay(RESOLUTION_RETRY_BASE_MS * attempt);
            }
        }
    }), [answer, conversationId, resolution.confirmation_id]);
    const promptText = (_b = resolution.prompt_text) !== null && _b !== void 0 ? _b : DEFAULT_PROMPT;
    const sections = [
        {
            content: (React.createElement(Text, { variant: "body", className: `${baseClass}__prompt` }, promptText))
        }
    ];
    if (answer !== null) {
        sections.push({
            content: (React.createElement(Text, { variant: "body", className: `${baseClass}__acknowledgment` }, answer === 'resolved' ? ACK_RESOLVED : ACK_UNRESOLVED))
        });
    }
    const footerGroups = answer === null
        ? [
            {
                actions: [
                    {
                        id: 'resolution-yes',
                        label: 'Yes',
                        variant: 'primary',
                        onClick: () => {
                            void handleAnswer(true);
                        }
                    },
                    {
                        id: 'resolution-no',
                        label: 'No',
                        variant: 'default',
                        onClick: () => {
                            void handleAnswer(false);
                        }
                    }
                ]
            }
        ]
        : [];
    return React.createElement(AIGeneralBlockCard, { className: baseClass, sections: sections, footerGroups: footerGroups });
}
